//* Vue
import type { SetupContext } from "vue";
//* 组件属性
import type { ModuleProps, ModuleEmits } from "../index";
//* 父组件样式
import css_father from "../../../index.module.less";

export const useComposable = (define: ModuleProps, emits: SetupContext<typeof ModuleEmits>["emit"]) => {
    //* 插件
    const i18n = useI18n();

    //* 静态属性
    const vars: { clientX: number; clientY: number } = {
        clientX: 0, //* 保存拖拽X
        clientY: 0, //* 保存拖拽Y
    };

    //* 响应式属性
    const refs = {
        visible: ref<boolean>(false), //* 是否展示
        view: ref<any>(),
    };

    //* 节点
    const nodes = {
        container: ref<HTMLDivElement>(),
        controls: ref<HTMLElement>(),
    };

    //* 配置项
    const options = {
        //* 绘图突变筛选项
        mutations: [
            { name: i18n.t("Pathogenic"), value: "Pathogenic" },
            { name: i18n.t("LikelyPathogenic"), value: "Likely Pathogenic" },
            { name: i18n.t("LikelyBenign"), value: "Likely Benign" },
            { name: i18n.t("Benign"), value: "Benign" },
            { name: i18n.t("Uncertain"), value: "Uncertain" },
        ],
    };

    //* 函数列表
    const methods = {
        //* 窗口关闭函数
        close: () => (refs.visible.value = false),
        //* 窗口激活函数
        open: (data: any, container: Element) => {
            //* 数据更新
            refs.view.value = data;
            refs.visible.value = true;
            nextTick(() => {
                if (!nodes.container.value) return;
                else {
                    //* 重置
                    vars.clientX = 0;
                    vars.clientY = 0;
                    nodes.container.value.style.transform = "";
                    //* 检测边界
                    methods.boundary(container, nodes.container.value as HTMLDivElement);
                }
            });
        },

        boundary: (node: Element, view: HTMLDivElement) => {
            //* 数据初始化
            const father = document.querySelector(`.${css_father.simplebar} .ui-simplebar-container`);
            const scroll = father?.scrollTop || 0;
            const rect = father?.getBoundingClientRect() || { width: 0, height: 0 };
            const config = {
                x: Number(node?.getAttribute("x")) || 0,
                y: Number(node?.getAttribute("y")) || 0,
                size: Number(node?.getAttribute("size")) || 0,
            };

            //* Y轴边界处理（底部超出绘图区域）
            if (config.y + view.offsetHeight - scroll > rect.height) config.y = scroll + rect.height - view.offsetHeight;
            //* Y轴边界处理（顶部超出绘图区域）
            if (config.y < scroll) config.y = scroll;
            //* X轴边界处理
            if (config.x + view.offsetWidth <= rect.width) config.x += config.size + 8;
            else {
                config.x = config.x - view.offsetWidth - config.size - 8;
            }

            view.style.left = `${config.x}px`;
            view.style.top = `${config.y}px`;
        },
    };

    //* 响应事件
    const ons = {
        drag: {
            mousedown: (ev: MouseEvent) => {
                if (!nodes.container.value || nodes.controls.value?.contains(ev.target as Node)) return false;
                else {
                    //* 获取容器位置
                    const rect = nodes.container.value.getBoundingClientRect();
                    //* 缓存点击位置
                    const offset = { clientX: ev.clientX, clientY: ev.clientY };
                    //* 缓存边界的位置
                    const boundary = {
                        min: -rect.left,
                        max: Math.floor(document.documentElement.clientWidth - nodes.container.value.offsetWidth - rect.left),
                    };

                    //* 移动鼠标拖拽事件
                    document.onmousemove = (ev) => {
                        if (!nodes.container.value) return false;
                        else {
                            //* 数据初始化
                            const offsetY = vars.clientY + (ev.clientY - offset.clientY);
                            const offsetX = vars.clientX + Math.max(Math.min(ev.clientX - offset.clientX, boundary.max), boundary.min);
                            //* 样式修改
                            nodes.container.value.style.transform = `translate(${offsetX}px, ${offsetY}px)`;
                        }
                    };

                    //* 松开鼠标事件
                    document.onmouseup = (ev) => {
                        //* 销毁事件
                        document.onmousemove = null;
                        document.onmouseup = null;
                        //* 保存当前拖拽后的位置
                        vars.clientX += Math.max(Math.min(ev.clientX - offset.clientX, boundary.max), boundary.min);
                        vars.clientY += ev.clientY - offset.clientY;
                    };
                }
            },
        },
    };

    return { vars, refs, nodes, methods, ons, options };
};
